import { type BoardPost } from '@talkofthetown/shared';
import { useMemo, type CSSProperties } from 'react';
import { useElementSize } from '../hooks/misc';
import { useBoardMotion } from '../hooks/useBoardMotion';
import { AdBubble, adsEnabled } from './AdBubble';
import { Whispers, type Box } from './Whispers';
import { formatDistance, READ_MORE, timeAgo } from '../lib/format';
import { fitTitle, layoutBoard, TITLE_LINE, tileParts, type TileRect } from '../lib/layout';
import type { BoardSnapshot } from '../lib/motion';

interface Action {
  label: string;
  onClick: () => void;
}

interface Props {
  posts: BoardPost[];
  /** The snapshot shown: a new one makes the board arrive, or (after Refresh) show what changed. */
  snapshot: BoardSnapshot;
  stale: boolean;
  now: number;
  /** Whispers (new and faded posts), appearing and disappearing all around the voices. */
  whispers: BoardPost[];
  /** Paid posts, whispered more (yellow, marked Sponsored). */
  sponsored: BoardPost[];
  /** What the whispers say when there are no voices here yet. */
  whisperNote?: string;
  /** What an empty board says, and where else it offers to look (a wider distance, a longer time). */
  empty: { text: string; actions: Action[] };
  onOpen: (post: BoardPost) => void;
  onCompose: () => void;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Where a bubble's tail is: bottom or top, left or right (the same for a post every time). */
const TAILS = ['bl', 'br', 'tl', 'tr'] as const;

/** Space between bubbles, px. */
const GAP = 10;

/**
 * Where the voices go when there are whispers around them, in % of the board: the middle, with
 * room on every side (above and below only, on a phone).
 */
const CENTRE: Box = { x: 24, y: 19, w: 52, h: 62 };
const CENTRE_PHONE: Box = { x: 0, y: 17, w: 100, h: 65 };

/**
 * The ad's place around the voices: the bottom of the right-hand column, or the band below them on
 * a phone (wide enough for an ad).
 */
const adPlace = (centre: Box, phone: boolean): Box =>
  phone
    ? { x: 0.5, y: centre.y + centre.h + 3, w: 99, h: 100 - centre.y - centre.h - 3.5 }
    : { x: centre.x + centre.w + 1.5, y: 67.6, w: 100 - centre.x - centre.w - 2, h: 31 };

/** A bubble's vertical padding in px [normal, compact], tail included, as in styles.css. */
const PADDING = [46, 36] as const;

/** The hand-lettered capitals' average width in ems, letter-spacing included (measured). */
const CHAR_WIDTH = 0.39;

/** Height of the meta line (author, AI tag) with its margin, in px. */
const META_PX = 22;

function describe(post: BoardPost, now: number, crowned: boolean): string {
  return [
    crowned ? 'Talk of the town' : null,
    post.title,
    post.isAgent ? `AI summary of ${post.author}` : `by ${post.author}`,
    timeAgo(post.createdAt, now),
    formatDistance(post.distanceKm),
  ]
    .filter(Boolean)
    .join(', ');
}

function hashOf(id: string): number {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return Math.abs(h);
}

/** AI summaries carry a ✦ (the tile's label says "AI summary"; so does the tooltip). */
const AiTag = ({ className = '' }: { className?: string }) => (
  <span className={`badge badge--ai ${className}`} title="AI summary">
    ✦
  </span>
);

/** Biggest title size per bubble size, px (the hand lettering is narrow, so it can go big). */
const TITLE_MAX = { crown: 76, xl: 58, lg: 42, md: 30, sm: 20 } as const;

/** One line of excerpt text, px (14px at 1.35, as in styles.css). */
const EXCERPT_LINE = 14 * 1.35;

/**
 * A voice: a speech bubble, text only (pictures show when it's opened), its tail on a side that
 * stays the same for the post. The talk of the town is coral, the loudest few pink, the rest white.
 */
function Tile({
  rect,
  crowned,
  loud,
  now,
  onOpen,
}: {
  rect: TileRect;
  crowned: boolean;
  loud: boolean;
  now: number;
  onOpen: (p: BoardPost) => void;
}) {
  const { post, x, y, w, h } = rect;
  const { size, tiny, compact, meta, aiTag } = tileParts(post, w, h);
  const crown = crowned && !tiny && h >= 80;
  const inner = h - PADDING[compact ? 1 : 0] - (meta ? META_PX : 0);
  // The headline fills the bubble: as big as fits, leaving a big bubble room for a couple of lines
  // of its description.
  const withExcerpt =
    (size === 'lg' || size === 'xl') && !!post.excerpt && 2 * EXCERPT_LINE + 6 <= inner * 0.4;
  const titleRoom = withExcerpt ? inner - 2 * EXCERPT_LINE - 6 : inner;
  const fit = fitTitle(
    post.title,
    w - (compact ? 24 : 36),
    titleRoom,
    compact ? 12 : 13,
    crown ? TITLE_MAX.crown : TITLE_MAX[size],
    5,
    CHAR_WIDTH,
  );
  const titleSize = fit.size;
  const lineH = titleSize * TITLE_LINE;
  const titleLines = clamp(Math.floor(inner / lineH), 1, 6);
  const excerptLines =
    size !== 'sm' && post.excerpt
      ? Math.max(0, Math.floor((inner - fit.lines * lineH - 6) / EXCERPT_LINE))
      : 0;
  const style = {
    translate: `${x}px ${y}px`,
    width: w,
    height: h,
    '--title-size': `${titleSize}px`,
    '--title-lines': titleLines,
    '--excerpt-lines': Math.min(excerptLines, 4),
  } as CSSProperties;
  const distance = formatDistance(post.distanceKm);

  return (
    <button
      type="button"
      className={`tile tile--${size}${compact ? ' tile--compact' : ''}${crown ? ' is-crowned' : ''}${loud && !crown ? ' is-loud' : ''}`}
      style={style}
      onClick={() => onOpen(post)}
      aria-label={describe(post, now, crown)}
      title={READ_MORE}
      data-post-id={post.id}
      data-kind={post.isAgent ? 'ai' : 'person'}
      data-tail={TAILS[hashOf(post.id) % 4]}
    >
      {crown && <span className="tile__shine" aria-hidden="true" />}
      <span className="tile__body" hidden={tiny}>
        <span className="tile__title">{post.title}</span>
        {excerptLines > 0 && <span className="tile__excerpt">{post.excerpt}</span>}
        {meta && (
          <span className="tile__meta">
            {aiTag === 'meta' && <AiTag className="badge--inline" />}
            {compact
              ? w >= 120
                ? post.author
                : null
              : `${post.author} · ${timeAgo(post.createdAt, now)}`}
            {!compact && distance && size === 'xl' ? ` · ${distance}` : ''}
          </span>
        )}
      </span>
      {aiTag === 'alone' && <AiTag className="tile__ai-only" />}
      {/* Says "New" or "▲ Rising" for a moment after a refresh (see useBoardMotion). */}
      <span className="tile__flag" aria-hidden="true" />
    </button>
  );
}

/** Placeholder bubbles while a board loads: [kind, tail, text lines]; places are in styles.css. */
const LOADING = [
  ['voice', 'bl', 3],
  ['voice', 'br', 2],
  ['voice', 'tr', 2],
  ['voice', 'tl', 2],
  ['whisper', 'bl', 2],
  ['whisper', 'br', 2],
  ['whisper', 'tl', 2],
  ['whisper', 'tr', 2],
] as const;

/** The board while it loads: the shape of what's coming, voices in the middle, whispers around. */
function BoardLoading() {
  return (
    <div className="board__loading" role="status">
      {LOADING.map(([kind, tail, lines], i) => (
        <span key={i} className={`loading-bubble loading-bubble--${kind}`} data-tail={tail}>
          {Array.from({ length: lines }, (_, j) => (
            <i key={j} style={{ animationDelay: `${(i * 3 + j) * 90}ms` }} />
          ))}
        </span>
      ))}
      <p className="board__loading-text">Listening to what’s being said…</p>
    </div>
  );
}

/**
 * The living front page: up to five voices in the middle, bubble size following how much people are
 * opening each right now (the post opened most, the talk of the town, the biggest and coral), and
 * the whispers appearing and disappearing all around them.
 */
export function Board({
  posts,
  snapshot,
  stale,
  now,
  whispers,
  sponsored,
  whisperNote,
  empty,
  onOpen,
  onCompose,
}: Props) {
  const [ref, size] = useElementSize<HTMLElement>();
  const centre = size.width < 640 ? CENTRE_PHONE : CENTRE;
  // One ad around the voices, when the site shows ads.
  const adSpot = posts.length > 0 && adsEnabled() ? adPlace(centre, size.width < 640) : null;
  // The voices take the middle, with the whispers (and the ad) around them; else the whole board.
  const ring = posts.length > 0 && (whispers.length + sponsored.length > 0 || !!adSpot);
  const box = ring
    ? {
        x: Math.round((size.width * centre.x) / 100),
        y: Math.round((size.height * centre.y) / 100),
        w: Math.round((size.width * centre.w) / 100),
        h: Math.round((size.height * centre.h) / 100),
      }
    : { x: 0, y: 0, w: size.width, h: size.height };
  // As many voices as fit readably (five at most); the rest join the whispers.
  const { x: bx, y: by, w: bw, h: bh } = box;
  const layout = useMemo(() => {
    const inBox = layoutBoard(posts, bw, bh, GAP);
    return { ...inBox, tiles: inBox.tiles.map((t) => ({ ...t, x: t.x + bx, y: t.y + by })) };
  }, [posts, bx, by, bw, bh]);
  const around = useMemo(() => [...whispers, ...layout.dropped], [whispers, layout.dropped]);
  // The next loudest after the talk of the town (two of four, one of fewer) are pink.
  const loud = useMemo(() => {
    const others = layout.tiles
      .filter((t) => t.post.id !== layout.crown)
      .sort((a, b) => b.w * b.h - a.w * a.h);
    const n = others.length >= 3 ? 2 : others.length > 0 ? 1 : 0;
    return new Set(others.slice(0, n).map((t) => t.post.id));
  }, [layout]);
  useBoardMotion(ref, layout, snapshot);

  return (
    <section
      ref={ref}
      className={`board${stale ? ' is-stale' : ''}`}
      aria-label="Most talked-about posts"
      aria-busy={stale}
    >
      {layout.tiles.map((t) => (
        <Tile
          key={t.post.id}
          rect={t}
          crowned={t.post.id === layout.crown}
          loud={loud.has(t.post.id)}
          now={now}
          onOpen={onOpen}
        />
      ))}
      {adSpot && (
        <AdBubble
          style={{
            left: `${adSpot.x}%`,
            top: `${adSpot.y}%`,
            width: `${adSpot.w}%`,
            height: `${adSpot.h}%`,
          }}
        />
      )}
      {around.length + sponsored.length > 0 && (
        <Whispers
          whispers={around}
          sponsored={sponsored}
          now={now}
          around={posts.length > 0 ? centre : undefined}
          reserved={adSpot ?? undefined}
          note={whisperNote}
          onOpen={onOpen}
        />
      )}
      {posts.length === 0 &&
        whispers.length === 0 &&
        sponsored.length === 0 &&
        (stale ? (
          <BoardLoading />
        ) : (
          <div className="board__empty">
            <p className="board__empty-title">It’s quiet here.</p>
            <p>
              {empty.text}{' '}
              {empty.actions.length > 0
                ? 'Look further, or be the first to get people talking.'
                : 'Be the first to get people talking.'}
            </p>
            <div className="board__empty-actions">
              {empty.actions.map((a) => (
                <button key={a.label} type="button" className="btn" onClick={a.onClick}>
                  {a.label}
                </button>
              ))}
              <button type="button" className="btn btn--primary" onClick={onCompose}>
                Start the talk
              </button>
            </div>
          </div>
        ))}
    </section>
  );
}
